/**
 * Streams a Care Delivery AI turn over Server-Sent Events.
 *
 * Wraps `POST /api/blueprint/chat` (an SSE endpoint). It sends the provider's
 * message, reads the streamed `data:` frames, parses each normalized
 * `ChatStreamEvent`, and surfaces the incrementally-assembled assistant text
 * plus the resolved thread id (from the `X-Thread-Id` response header) so a new
 * thread can be tracked.
 *
 * Reliability: `send` accepts an optional `AbortSignal` so a superseded turn
 * (patient/thread switch) can cancel its in-flight stream (B2). A 404 on a
 * resumed thread (stale/deleted) is reported via `staleThread` so the caller can
 * retry as a fresh thread (B5). A non-OK response is logged as an ERROR to the
 * client-log transport so chat failures are observable in Cloud Logging (B6).
 * Message content is never logged (HIPAA).
 */
import { useCallback, useRef, useState } from "react";
import config from "../config";
import logger from "../utils/logger";
import type { ChatStreamEvent } from "../types";

/** The streaming lifecycle status. */
export type StreamStatus = "idle" | "streaming" | "done" | "error";

/** Arguments for {@link UseChatStreamResult.send}. */
export interface SendArgs {
  /** The patient the conversation is scoped to. */
  patientId: string;
  /** The provider's message text. */
  message: string;
  /** Existing thread to resume, or null/undefined to start a new one. */
  threadId?: string | null;
  /**
   * Optional abort signal. When aborted (patient/thread switch), the in-flight
   * fetch + stream read are cancelled and the turn resolves as aborted.
   */
  signal?: AbortSignal;
  /** Thinking budget for model reasoning (0 for fast mode / default). */
  thinkingBudget?: number;
}

/** The outcome of a completed (or failed) streamed turn. */
export interface StreamResult {
  /** The assistant's final assembled text (empty on error). */
  text: string;
  /** The thread id resolved from the `X-Thread-Id` header, if any. */
  threadId: string | null;
  /**
   * True when the resumed thread was stale (404) — the caller should drop the
   * thread id and retry as a new thread (B5).
   */
  staleThread?: boolean;
  /** True when the turn was aborted (superseded by a patient/thread switch). */
  aborted?: boolean;
}

/** The result surface returned by {@link useChatStream}. */
export interface UseChatStreamResult {
  /** The current streaming status. */
  status: StreamStatus;
  /** The assistant text assembled so far for the in-flight turn. */
  streamedText: string;
  /** The thread id resolved from the response, if known. */
  threadId: string | null;
  /** Start streaming a turn; resolves with the final text + thread id. */
  send: (args: SendArgs) => Promise<StreamResult>;
  /** Clear streamed text (e.g. after persisting the turn). */
  reset: () => void;
}

const CHAT_ENDPOINT = "/api/blueprint/chat";

/**
 * Parse one SSE `data:` frame into a normalized chat event.
 *
 * @param frame - A single SSE frame (may contain a `data:` line).
 * @returns The parsed event, or null when the frame carries no JSON payload.
 */
export function parseSseFrame(frame: string): ChatStreamEvent | null {
  const line = frame
    .split("\n")
    .map((l) => l.trim())
    .find((l) => l.startsWith("data:"));
  if (!line) return null;
  const payload = line.slice("data:".length).trim();
  if (payload === "") return null;
  try {
    return JSON.parse(payload) as ChatStreamEvent;
  } catch (err) {
    logger.devOnly.warn("useChatStream: bad SSE frame", { error: String(err) });
    return null;
  }
}

/**
 * Read an SSE response body to completion, dispatching each parsed frame.
 *
 * Stops early if the optional `signal` aborts mid-stream, cancelling the
 * reader so a superseded turn does not keep consuming.
 *
 * @param body - The streaming response body.
 * @param onEvent - Invoked with every parsed chat event.
 * @param signal - Optional abort signal; stops the read loop when aborted.
 * @returns A promise that resolves once the stream is fully consumed or aborted.
 */
async function consumeStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: ChatStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    if (signal?.aborted) {
      // Defensive: cancel() rejects only if the stream is already errored.
      await reader.cancel().catch(/* v8 ignore next */ () => undefined);
      return;
    }
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split("\n\n");
    buffer = frames.pop() ?? "";
    for (const frame of frames) {
      const event = parseSseFrame(frame);
      if (event) onEvent(event);
    }
  }
  const trailing = parseSseFrame(buffer);
  if (trailing) onEvent(trailing);
}

/**
 * Drive a single SSE chat turn against the backend.
 *
 * @returns The streaming status, assembled text, thread id, and a `send` fn.
 */
export function useChatStream(): UseChatStreamResult {
  const [status, setStatus] = useState<StreamStatus>("idle");
  const [streamedText, setStreamedText] = useState("");
  const [threadId, setThreadId] = useState<string | null>(null);
  const textRef = useRef("");

  const reset = useCallback(() => {
    textRef.current = "";
    setStreamedText("");
    setStatus("idle");
  }, []);

  const applyEvent = useCallback((event: ChatStreamEvent): void => {
    if (event.type === "text_delta") {
      textRef.current += event.text;
      setStreamedText(textRef.current);
    }
  }, []);

  const send = useCallback(
    async ({
      patientId,
      message,
      threadId: resume,
      signal,
      thinkingBudget,
    }: SendArgs): Promise<StreamResult> => {
      textRef.current = "";
      setStreamedText("");
      setStatus("streaming");

      let response: Response;
      try {
        response = await fetch(`${config.BACKEND_URL ?? ""}${CHAT_ENDPOINT}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            "X-Patient-ID": patientId,
          },
          body: JSON.stringify({
            patient_id: patientId,
            message,
            thread_id: resume ?? null,
            thinking_budget: thinkingBudget ?? 0,
          }),
          signal,
        });
      } catch (err) {
        // An aborted fetch (patient/thread switch) is expected — resolve quietly
        // as aborted so the caller drops the superseded turn without an error.
        if (signal?.aborted) return { text: "", threadId: null, aborted: true };
        throw err;
      }

      const resolvedThread = response.headers.get("X-Thread-Id");
      if (resolvedThread) setThreadId(resolvedThread);

      // B5: a resumed thread that 404s is stale/deleted — signal the caller to
      // retry as a fresh thread instead of surfacing a dead-end stream error.
      if (response.status === 404 && resume) {
        setStatus("error");
        return { text: "", threadId: null, staleThread: true };
      }

      if (!response.ok || !response.body) {
        // B6: surface chat stream failures in Cloud Logging (jsonPayload.frontend.*)
        // — there were previously zero frontend chat logs. No PHI is logged.
        logger.error(
          "chat stream request failed",
          new Error(`chat stream HTTP ${response.status}`),
        );
        setStatus("error");
        return { text: "", threadId: resolvedThread };
      }

      await consumeStream(response.body, applyEvent, signal);
      if (signal?.aborted) {
        setStatus("idle");
        return {
          text: textRef.current,
          threadId: resolvedThread,
          aborted: true,
        };
      }
      setStatus("done");
      return { text: textRef.current, threadId: resolvedThread };
    },
    [applyEvent],
  );

  return { status, streamedText, threadId, send, reset };
}
